import type { Hooks, ToolUse } from './hooks';
import type { HostBridge, AgentTool } from './tools';
import { posix } from 'node:path';
import { globToRegExp } from './tools.structured';

/**
 * Permissions & plan-mode — safe-autonomy controls built ENTIRELY on the hooks +
 * host seams (no Agent-core changes needed). Two pieces:
 *   - PermissionPolicy → Hooks: allow / ask (host.confirm) / deny a tool call by tool
 *     name + path glob. First matching rule wins; default decision otherwise.
 *   - planMode(): blocks mutating tools until the agent presents a plan via `ExitPlanMode`
 *     and the host approves — Claude-Code-style plan gating.
 */
export type Decision = 'allow' | 'ask' | 'deny';

/** Match a shell command against a rule glob where `*` spans anything (including spaces and `/`),
 *  e.g. `git *` → any git subcommand, `rm *` → any rm invocation. Literals are regex-escaped. */
function commandMatches(glob: string, cmd: string): boolean {
  const pattern = glob.split('*').map((s) => s.replace(/[.+?^${}()|[\]\\]/g, '\\$&')).join('.*');
  return new RegExp('^' + pattern + '$').test(cmd);
}

/** Shell control syntax (chaining, pipes, substitution, redirection): an allow-list's `Bash(cmd *)` grants that command only. */
const SHELL_CONTROL = /[;&|`$<>\n\r]/;

/** Rule tool name vs call name; `*` is a wildcard (`mcp__browser-bridge__*` = every tool of that server). */
function toolMatches(rule: string, name: string): boolean {
  return rule.includes('*') ? commandMatches(rule, name) : rule === name;
}

export interface PermissionRule {
  /** tool name to match (omit = any tool). */
  tool?: string;
  /** glob on the call's `path` arg to match (omit = any/none). */
  pathGlob?: string;
  decision: Decision;
  /** Set by the caller for per-run rules (CLI `--disallowedTools`/`--allowedTools`), vs rules loaded from config files. */
  explicit?: boolean;
}

export class PermissionOptions {
  rules: PermissionRule[] = [];
  /** decision when no rule matches. 'deny' = an allow-list run (`--onlyTools`): only the allow rules run, an allowed
   *  command glob never matches shell control syntax, and claude-code enforces it by a PreToolUse hook (ccHooks). */
  default: Decision = 'allow';
  /** host channel for `ask` (confirm). If absent, `ask` is treated as `deny` (fail-closed). */
  host?: HostBridge;
  /** Richer ask resolver (CLI surfaces a Yes/No/Always menu). Returns the decision + whether to
   *  remember it — on remember, the policy adds a session rule so the same tool isn't re-asked.
   *  If unset, falls back to `host.confirm` (plain yes/no). */
  ask?(call: ToolUse): Promise<{ decision: 'allow' | 'deny'; remember?: boolean } | undefined>;
}

export class PermissionPolicy {
  public options: PermissionOptions;
  constructor(options?: Partial<PermissionOptions>) {
    this.options = { ...new PermissionOptions(), ...options };
  }

  /** Resolve the decision for a tool call (first matching rule, else default). */
  decide(call: ToolUse): Decision {
    // Deferred MCP (`McpCall({name})`) routes many tools through ONE agent tool — rules written
    // against the underlying `mcp__server__tool` name must still match (deny rules especially).
    const names = call.name === 'McpCall' && typeof call.args?.name === 'string' ? [call.name, call.args.name] : [call.name];
    if (CC_TOOL_ALIAS[call.name]) names.push(CC_TOOL_ALIAS[call.name]); // `Bash(…)` rules hold for agentx's Shell/bash too
    const strict = this.options.default === 'deny';
    for (const r of this.options.rules) {
      if (r.tool && !names.some((n) => toolMatches(r.tool!, n))) continue;
      if (r.pathGlob) {
        // The glob matches a file `path` (path-glob semantics, slash-normalized) OR a `command`
        // string (`Shell(git *)`/`bash(rm *)`) — commands use *=anything (slashes included).
        const path = typeof call.args?.path === 'string' ? call.args.path : null;
        const cmd = typeof call.args?.command === 'string' ? call.args.command : null;
        if (path != null) {
          if (!globToRegExp(r.pathGlob).test(posix.normalize(path.startsWith('/') ? path : `/${path}`))) continue; // `dir/../x` can't leave dir
        } else if (cmd != null) {
          if (!commandMatches(r.pathGlob, cmd) || (strict && r.decision === 'allow' && SHELL_CONTROL.test(cmd))) continue;
        } else continue; // rule has a glob but the call exposes neither path nor command
      }
      return r.decision;
    }
    return this.options.default;
  }

  /** The policy's `deny` rules as rule strings (`Tool` / `Tool(glob)`) — for delegated runtimes that run
   *  their OWN tools (claude-code/cursor) and never pass through {@link hooks}. */
  denyRules(explicitOnly = false): string[] {
    return this.options.rules.filter((r) => r.decision === 'deny' && (!explicitOnly || r.explicit)).map((r) => `${r.tool ?? '*'}${r.pathGlob ? `(${r.pathGlob})` : ''}`);
  }

  /** An allow-list policy (default 'deny') as Claude Code SDK hooks: claude-code runs its own tools, which never reach
   *  {@link hooks}, and `disallowedTools` can't say "everything else". Every tool call (native and MCP) goes through
   *  decide(); `ask` has no host there → deny. undefined for a default-allow policy (its deny rules travel as disallowedTools). */
  ccHooks(): Record<string, unknown[]> | undefined {
    if (this.options.default !== 'deny') return undefined;
    const gate = async (input: any) => {
      const t = input?.tool_input ?? {}, path = t.file_path ?? t.notebook_path ?? t.path;
      const d = this.decide({ name: String(input?.tool_name ?? ''), args: { ...t, ...(typeof path === 'string' ? { path } : {}) } } as ToolUse);
      return d === 'allow' ? {} : { hookSpecificOutput: { hookEventName: 'PreToolUse', permissionDecision: 'deny', permissionDecisionReason: `not on this run's tool allow-list (${input?.tool_name})` } };
    };
    return { PreToolUse: [{ hooks: [gate] }] };
  }

  /** A preToolUse hook enforcing this policy (deny/ask → block; allow → proceed). */
  hooks(): Hooks {
    return {
      preToolUse: async (call) => {
        const d = this.decide(call);
        if (d === 'allow') return;
        if (d === 'deny') return { block: true, reason: `denied by permission policy (${call.name})` };
        // ask — prefer the richer resolver (Yes/No/Always); fall back to a plain confirm.
        if (this.options.ask) {
          const r = await this.options.ask(call);
          const decision = r?.decision ?? 'deny';
          // Remember the UNDERLYING tool for deferred MCP — remembering 'McpCall' itself would
          // blanket-approve every MCP tool from one answer. decide() aliases it back on match.
          const rememberTool = call.name === 'McpCall' && typeof call.args?.name === 'string' ? call.args.name : call.name;
          if (r?.remember) this.options.rules.unshift({ tool: rememberTool, decision }); // session-remember (wins next time)
          return decision === 'allow' ? undefined : { block: true, reason: `not approved (${call.name})` };
        }
        const ok = await this.options.host?.confirm?.(`Allow ${call.name}${call.args?.path ? ' on ' + call.args.path : ''}?`);
        return ok ? undefined : { block: true, reason: `not approved (${call.name})` };
      },
    };
  }
}

/** agentx shell tool names → Claude Code's. CC calls its shell `Bash`; agentx's are `Shell` (real) and `bash` (VFS). */
const CC_TOOL_ALIAS: Record<string, string> = { Shell: 'Bash', bash: 'Bash' };

/**
 * A policy's deny rules as Claude Code `disallowedTools` entries (deduped; agentx shell names mapped to
 * `Bash`). The Claude Agent SDK removes these from the model's context and refuses them even under
 * `bypassPermissions`. A bare `*` (deny everything) has no CC spelling, so it throws — fail closed.
 */
export function claudeCodeDisallowedTools(policy?: PermissionPolicy): string[] {
  const out = new Set<string>();
  for (const rule of policy?.denyRules() ?? []) {
    const m = /^([^(]+)(\(.*\))?$/.exec(rule)!;
    if (m[1] === '*') throw new Error('permission policy denies every tool (`*`) — claude-code cannot express that; refusing to run.');
    out.add((CC_TOOL_ALIAS[m[1]] ?? m[1]) + (m[2] ?? ''));
  }
  return [...out];
}

/** Tools the agent must not run before the plan is approved (sensible default). */
export const DEFAULT_MUTATING = ['Write', 'Edit', 'MultiEdit', 'deleteFile', 'bash'];

/**
 * Plan-mode: mutating tools are BLOCKED until the agent calls `ExitPlanMode` with a plan
 * and (if a host is present) the user approves it. Returns the gating hook + the tool to
 * add to the agent. Compose the hook with others via composeHooks().
 */
export function planMode(opts?: { mutating?: string[]; host?: HostBridge }): { hooks: Hooks; tool: AgentTool } {
  const mutating = new Set(opts?.mutating ?? DEFAULT_MUTATING);
  let approved = false;
  const tool: AgentTool = {
    name: 'ExitPlanMode',
    description: 'Call when your plan is ready and you want to start making changes. Provide the `plan`. Until this is approved, edits/writes are blocked.',
    parameters: { type: 'object', required: ['plan'], properties: { plan: { type: 'string', description: 'the concrete steps you will take' } } },
    async run({ plan }, _ctx) {
      if (opts?.host?.confirm) {
        const confirm = opts.host.confirm(`Approve this plan?\n\n${String(plan ?? '')}`);
        const ok = await (_ctx?.parkHuman ? _ctx.parkHuman(confirm) : confirm); // exclude approval wait from the timeout
        if (!ok) return 'Plan not approved. Revise it and call ExitPlanMode again.';
      }
      approved = true;
      return 'Plan approved — you may now make changes.';
    },
  };
  const hooks: Hooks = {
    preToolUse: (call) =>
      !approved && mutating.has(call.name)
        ? { block: true, reason: 'plan mode: present a plan and call ExitPlanMode (approved) before editing.' }
        : undefined,
  };
  return { hooks, tool };
}

/** Run several Hooks as one: preToolUse stops at the first block; post/stop all fire. */
export function composeHooks(...list: (Hooks | undefined)[]): Hooks {
  const hooks = list.filter(Boolean) as Hooks[];
  return {
    async preToolUse(call, meta) {
      for (const h of hooks) {
        const d = await h.preToolUse?.(call, meta);
        if (d?.block) return d;
      }
    },
    async postToolUse(call, result, meta) { for (const h of hooks) await h.postToolUse?.(call, result, meta); },
    onToolOutput(call, chunk, meta) { for (const h of hooks) h.onToolOutput?.(call, chunk, meta); },
    onStop(text) { for (const h of hooks) h.onStop?.(text); },
    // lifecycle: concatenate session-start context; chain prompt-submit transforms; fan out pre-compact.
    async onSessionStart() {
      let ctx = '';
      for (const h of hooks) { const r = await h.onSessionStart?.(); if (r) ctx += (ctx ? '\n\n' : '') + r; }
      return ctx || undefined;
    },
    async onUserPromptSubmit(text) {
      let t = text;
      for (const h of hooks) { const r = await h.onUserPromptSubmit?.(t); if (typeof r === 'string') t = r; }
      return t;
    },
    async onPreCompact(messages) { for (const h of hooks) await h.onPreCompact?.(messages); },
    async onSubagentStop(summary, info) { for (const h of hooks) await h.onSubagentStop?.(summary, info); },
  };
}
